import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { endpoints } from '../api/endpoints'
import { Pagination, PageHeader } from '../components/Page'
import { actorLabel, AUDIT_CATEGORIES, auditCategory, describeAudit, type AuditCategory } from '../audit'
import { errorMessage, t } from '../strings'

const PAGE_SIZE = 15
const day = new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium' })
const clock = new Intl.DateTimeFormat('id-ID', { timeStyle: 'short' })

/**
 * Riwayat Perubahan: who changed what and when, newest first. A table on a wide screen, cards on a phone;
 * 15 to a page, with a filter for the kind of change.
 */
export function HistoryPage() {
  const audit = useQuery({ queryKey: ['audit'], queryFn: endpoints.audit })
  const [filter, setFilter] = useState<AuditCategory | 'all'>('all')
  const [page, setPage] = useState(1)

  const all = audit.data ?? []
  const rows = filter === 'all' ? all : all.filter((r) => auditCategory(r.action) === filter)
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const current = Math.min(page, pages) // the filter can leave fewer pages than the one we were on
  const shown = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE)

  const choose = (next: AuditCategory | 'all') => {
    setFilter(next)
    setPage(1)
  }

  return (
    <>
      <PageHeader title={t.historyTitle} subtitle={t.historySub} back={{ to: '/pengaturan', label: t.navSettings }} />

      <div className="chips" role="group" aria-label={t.historyFilter}>
        {[{ key: 'all' as const, label: t.historyAll }, ...AUDIT_CATEGORIES].map((c) => (
          <button key={c.key} type="button" className={filter === c.key ? 'chip on' : 'chip'} aria-pressed={filter === c.key} onClick={() => choose(c.key)}>
            {c.label}
          </button>
        ))}
      </div>

      {audit.isError && <p className="error">{errorMessage(audit.error.message)}</p>}
      {audit.isPending && <p className="muted">{t.loading}</p>}
      {audit.isSuccess && all.length === 0 && <p className="card muted">{t.historyEmpty}</p>}
      {audit.isSuccess && all.length > 0 && rows.length === 0 && <p className="card muted">{t.historyFilterEmpty}</p>}

      {shown.length > 0 && (
        <div className="card table-card">
          <table className="data-table history-table">
            <thead>
              <tr>
                <th scope="col">{t.colWhen}</th>
                <th scope="col">{t.colWhat}</th>
                <th scope="col">{t.colBy}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const at = new Date(r.created_at)
                return (
                  <tr key={r.id}>
                    <td data-label={t.colWhen} className="nowrap">
                      <span className="when-day">{day.format(at)}</span>
                      <span className="muted small when-time">{clock.format(at)}</span>
                    </td>
                    <td data-label={t.colWhat}>{describeAudit(r)}</td>
                    <td data-label={t.colBy} className="muted">
                      {actorLabel(r.actor_type)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={current} pageSize={PAGE_SIZE} total={rows.length} onPage={setPage} />
      {all.length >= 500 && <p className="muted small">{t.historyLimit}</p>}
    </>
  )
}
